import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Box, Typography, Skeleton, Stack, IconButton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
    getGetProcessActivitiesHistoricQueryOptions,
    getGetProcessInstanceIncidentsQueryOptions
} from "../../../../../api/bpm/endpoints";
import { useAuth } from "../../../../../auth";

import BPMNDiagram, { ActivityMarker } from "../../../../../components/BPMN/BPMNDiagram";
import TaskDetailsPanel from "./components/TaskDetailsPanel";
import { decodeNavParams } from "../../../../../utils/navParams";

const historySearchSchema = z.object({
    from: z.string().optional(),
    fromParams: z.string().optional(),
});

export const Route = createFileRoute('/__auth/processes/$processInstanceId/history/details')({
    component: DetailsPage,
    validateSearch: (search) => historySearchSchema.parse(search),
    staticData: {
        title: "routes.processDetails",
    },
});

function DetailsPage() {
    const { processInstanceId } = Route.useParams();
    const navigate = useNavigate();
    const search = Route.useSearch();
    const { currentTenant } = useAuth();

    const handleBack = () => {
        if (search.from === 'tasks' && search.fromParams) {
            const params = decodeNavParams(search.fromParams);
            navigate({ to: '/tasks', search: params as any });
        } else if (search.from === 'processes' && search.fromParams) {
            const params = decodeNavParams(search.fromParams);
            navigate({ to: '/processes', search: params as any });
        } else {
            navigate({ to: '..' });
        }
    };

    const { data: activitiesBlob, isLoading: isLoadingActivities } = useQuery({
        ...getGetProcessActivitiesHistoricQueryOptions(processInstanceId, {
            firstResult: 0,
            maxResults: 500,
            sortBy: 'startTime',
            sortOrder: 'asc'
        }),
        enabled: !!processInstanceId && !!currentTenant
    });

    const { data: incidentsBlob, isLoading: isLoadingIncidents } = useQuery({
        ...getGetProcessInstanceIncidentsQueryOptions(processInstanceId),
        enabled: !!processInstanceId && !!currentTenant
    });
    const incidents = (incidentsBlob as any)?.data || incidentsBlob || [];
    const incidentActivityIds = new Set<string>(
        incidents.map((inc: any) => inc.activityId).filter(Boolean)
    );

    const isLoading = isLoadingActivities || isLoadingIncidents;

    const activitiesData = activitiesBlob as any;
    const bpmnXml = activitiesData?.bpmnDiagram;

    const activities = activitiesData?.content || [];

    const activityMarkers: ActivityMarker[] = (() => {
        const markerMap: Record<string, ActivityMarker> = {};
        
        incidentActivityIds.forEach((actId) => {
            markerMap[actId] = {
                activityId: actId,
                hasIncident: true,
                isExpired: false,
                finished: false,
            };
        });

        activities.forEach((a: any) => {
            if (!a.activityId) return;
            const existing = markerMap[a.activityId];
            markerMap[a.activityId] = {
                activityId: a.activityId,
                hasIncident: existing?.hasIncident || !!a.hasIncident || incidentActivityIds.has(a.activityId),
                isExpired: existing?.isExpired || !!a.isExpired,
                finished: (existing?.finished !== false) ? !!a.endTime : false,
            };
        });
        return Object.values(markerMap);
    })();

    const highlightedActivityIds = activityMarkers.map(m => m.activityId);

    const activeActivities = activities.filter((a: any) => !a.endTime && a.activityId);
    const focusedActivityId = activeActivities.length > 0
        ? activeActivities.sort((a: any, b: any) => new Date(b.startTime || 0).getTime() - new Date(a.startTime || 0).getTime())[0].activityId
        : undefined;

    const executionCounts = activities.reduce((acc: Record<string, number>, curr: any) => {
        if (curr.activityId) {
            acc[curr.activityId] = (acc[curr.activityId] || 0) + 1;
        }
        return acc;
    }, {});

    const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
    const [selectedRuntimeActivityId, setSelectedRuntimeActivityId] = useState<string | null>(null);

    const activityExecutions = activities
        .filter((a: any) => a.activityId === selectedActivityId)
        .sort((a: any, b: any) => new Date(a.startTime || 0).getTime() - new Date(b.startTime || 0).getTime());

    useEffect(() => {
        if (focusedActivityId && !selectedActivityId) {
            setSelectedActivityId(focusedActivityId);
        }
    }, [focusedActivityId, selectedActivityId]);

    useEffect(() => {
        if (activityExecutions.length > 0) {
            const latest = activityExecutions[activityExecutions.length - 1];
            if (!selectedRuntimeActivityId || !activityExecutions.find((e: any) => e.id === selectedRuntimeActivityId)) {
                setSelectedRuntimeActivityId(latest.id || null);
            }
        } else {
            setSelectedRuntimeActivityId(null);
        }
    }, [selectedActivityId, activities]);

    const { data: variablesResponse } = useQuery({
        queryKey: ['activityVariables', selectedRuntimeActivityId],
        queryFn: async () => {
            if (!selectedRuntimeActivityId) return null;

            const currentExec = activityExecutions.find((e: any) => e.id === selectedRuntimeActivityId);
            const isActive = currentExec && !currentExec.endTime;
            const taskId = currentExec?.taskId;

            let url = '';
            if (isActive && taskId) {
                url = `/api/smartbpm/engine-rest/task/${taskId}/variables?deserializeValues=false`;
            } else {
                url = `/api/smartbpm/engine-rest/history/detail?activityInstanceId=${selectedRuntimeActivityId}&deserializeValues=false`;
            }

            const getStoredToken = () => {
                try {
                    const ls = localStorage.getItem("token");
                    if (ls) return JSON.parse(ls).accessToken;
                    const ss = sessionStorage.getItem("token");
                    if (ss) return JSON.parse(ss).accessToken;
                } catch { return ''; }
                return '';
            };

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${getStoredToken()}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch variables: ${response.statusText}`);
            }

            const json = await response.json();

            if (isActive && taskId) {
                return Object.entries(json).map(([key, val]: [string, any]) => ({
                    variableName: key,
                    value: val.value,
                    variableType: val.type,
                    ...val
                }));
            }

            return json;
        },
        enabled: !!selectedActivityId
    });

    const handleElementClick = (elementId: string) => {
        setSelectedActivityId(elementId);
        setSelectedRuntimeActivityId(null);
    };

    const currentExecution = activityExecutions.find((a: any) => a.id === selectedRuntimeActivityId) || activityExecutions[activityExecutions.length - 1];

    if (isLoading) {
        return (
            <Box>
                <Box sx={{ height: '45vh', p: 1, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', mb: 2 }}>
                    <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
                </Box>
                <Box sx={{ mt: 2, mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <Box sx={{ width: '60%' }}>
                            <Skeleton variant="text" height={40} width="40%" />
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Skeleton variant="rounded" width={80} height={24} />
                                <Skeleton variant="rounded" width={80} height={24} />
                            </Box>
                        </Box>
                        <Skeleton variant="rectangular" width={120} height={60} sx={{ borderRadius: 1 }} />
                    </Box>
                    <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1, mb: 3 }} />
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                </Box>
            </Box>
        );
    }

    return (
        <Stack spacing={2} sx={{ height: "100%", p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton onClick={handleBack} size="small">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5">
                    Detalhes do Processo
                </Typography>
            </Stack>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ height: '45vh', border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden' }}>
                    {bpmnXml ? (
                        <BPMNDiagram
                            xml={bpmnXml}
                            activityMarkers={activityMarkers}
                            highlightedActivityIds={highlightedActivityIds as string[]}
                            focusedActivityId={focusedActivityId}
                            selectedActivityId={selectedActivityId}
                            executionCounts={executionCounts}
                            onElementClick={handleElementClick}
                        />
                    ) : (
                        <Typography sx={{ p: 2, color: 'text.secondary' }}>Diagrama não disponível.</Typography>
                    )}
                </Box>

                {currentExecution && (
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <TaskDetailsPanel
                            activity={currentExecution}
                            variables={Array.isArray(variablesResponse) ? variablesResponse : (variablesResponse as any)?.variables || (variablesResponse as any)?.activityInstance?.variables || []}
                            allExecutions={activityExecutions}
                            selectedExecutionId={selectedRuntimeActivityId || currentExecution.id}
                            onExecutionChange={setSelectedRuntimeActivityId}
                        />
                    </Box>
                )}
            </Box>
        </Stack>
    );
}
